import {
  addDays,
  addWeeks,
  differenceInHours,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  getWeekOfMonth,
  parse,
  setHours,
  setMinutes,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { stringify } from "query-string";
import { convertDateRangeToEpochTimestamp } from "../../helpers/utils";
import type { ComparisonMode, ComparisonOption, DaySubRange } from "./types";

type DateFormatString = "yyyy-MM-dd" | "dd-MM-yyyy";

const createDateFormatter = (formatString: DateFormatString) => (date: Date) =>
  format(date, formatString);

const createDateParser = (formatString: "yyyy-MM-dd") => (dateString: string) =>
  parse(dateString, formatString, new Date());

export const parseDate = createDateParser("yyyy-MM-dd");

export const getComparisonMode = ({
  startDate,
  endDate,
  startTime,
  endTime,
}: {
  startDate: string;
  endDate: string;
  startTime: [number, number];
  endTime: [number, number];
}): ComparisonMode | null => {
  const startDateTime = setMinutes(
    setHours(parse(startDate, "yyyy-MM-dd", new Date()), startTime[0]),
    startTime[1]
  );
  const endDateTime = setMinutes(
    setHours(parse(endDate, "yyyy-MM-dd", new Date()), endTime[0]),
    endTime[1]
  );
  const deltaHours = differenceInHours(endDateTime, startDateTime);
  const fmt = createDateFormatter("dd-MM-yyyy");
  const timesMatch = JSON.stringify(startTime) === JSON.stringify(endTime);

  if (fmt(startOfYear(startDateTime)) === fmt(startDateTime) &&
      fmt(startOfYear(endDateTime)) === fmt(endDateTime) && timesMatch) {
    return "year";
  }
  if (fmt(startOfQuarter(startDateTime)) === fmt(startDateTime) &&
      fmt(startOfQuarter(endDateTime)) === fmt(endDateTime) && timesMatch) {
    return "quarter";
  }
  if (fmt(startOfMonth(startDateTime)) === fmt(startDateTime) &&
      fmt(startOfMonth(endDateTime)) === fmt(endDateTime) && timesMatch) {
    return "month";
  }
  if (fmt(startOfWeek(startDateTime)) === fmt(startDateTime) &&
      fmt(startOfWeek(endDateTime)) === fmt(endDateTime) && timesMatch) {
    return "week";
  }
  if (deltaHours === 24) {
    return "day";
  }
  return null;
};

export const buildCompareDateRange = ({
  baseDateRange,
  mode,
  option,
  count,
  subRange,
  customFrom,
  customTo,
}: {
  baseDateRange: { startDate: Date; endDate: Date };
  mode: ComparisonMode;
  option: ComparisonOption;
  count?: number;
  subRange?: DaySubRange;
  customFrom?: string;
  customTo?: string;
}): [string, string] => {
  const fmt = createDateFormatter("yyyy-MM-dd");

  switch (mode) {
    case "year":
      switch (option) {
        case "previous":
          return [fmt(subYears(baseDateRange.startDate, 1)), fmt(subYears(baseDateRange.endDate, 1))];
        case "last_n": {
          const c = count || 2;
          const ranges = Array(c).fill(null).map((_, i) => [
            fmt(subYears(baseDateRange.startDate, i + 1)),
            fmt(subYears(baseDateRange.endDate, i + 1)),
          ]);
          return [ranges[c - 1][0], ranges[0][1]];
        }
        case "custom": {
          if (customFrom) {
            const d = parse(customFrom, "yyyy-MM-dd", new Date());
            return [fmt(startOfYear(d)), fmt(addDays(endOfYear(d), 1))];
          }
          return ["", ""];
        }
        default:
          return ["", ""];
      }

    case "quarter":
      switch (option) {
        case "previous":
          return [fmt(subQuarters(baseDateRange.startDate, 1)), fmt(subQuarters(baseDateRange.endDate, 1))];
        case "same_last":
          return [fmt(subYears(baseDateRange.startDate, 1)), fmt(subYears(baseDateRange.endDate, 1))];
        case "last_n": {
          const c = count || 2;
          const ranges = Array(c).fill(null).map((_, i) => [
            fmt(subQuarters(baseDateRange.startDate, i + 1)),
            fmt(subQuarters(baseDateRange.endDate, i + 1)),
          ]);
          return [ranges[c - 1][0], ranges[0][1]];
        }
        case "custom": {
          if (customFrom) {
            const d = parse(customFrom, "yyyy-MM-dd", new Date());
            return [fmt(startOfQuarter(d)), fmt(addDays(endOfQuarter(d), 1))];
          }
          return ["", ""];
        }
        default:
          return ["", ""];
      }

    case "month":
      switch (option) {
        case "previous":
          return [fmt(subMonths(baseDateRange.startDate, 1)), fmt(subMonths(baseDateRange.endDate, 1))];
        case "same_last":
          return [fmt(subYears(baseDateRange.startDate, 1)), fmt(subYears(baseDateRange.endDate, 1))];
        case "last_n": {
          const c = count || 2;
          const ranges = Array(c).fill(null).map((_, i) => [
            fmt(subMonths(baseDateRange.startDate, i + 1)),
            fmt(subMonths(baseDateRange.endDate, i + 1)),
          ]);
          return [ranges[c - 1][0], ranges[0][1]];
        }
        case "custom": {
          if (customFrom) {
            const d = parse(customFrom, "yyyy-MM-dd", new Date());
            return [fmt(d), fmt(addDays(endOfMonth(d), 1))];
          }
          return ["", ""];
        }
        default:
          return ["", ""];
      }

    case "week":
      switch (option) {
        case "previous":
          return [fmt(subDays(baseDateRange.startDate, 7)), fmt(subDays(baseDateRange.endDate, 7))];
        case "same_last": {
          const weekOfMonth = getWeekOfMonth(baseDateRange.startDate);
          const prevMonth = new Date(baseDateRange.startDate.getFullYear(), baseDateRange.startDate.getMonth() - 1, 1);
          return [
            fmt(startOfWeek(addWeeks(prevMonth, weekOfMonth - 1))),
            fmt(addDays(endOfWeek(addWeeks(prevMonth, weekOfMonth - 1)), 1)),
          ];
        }
        case "last_n": {
          const c = count || 2;
          const ranges = Array(c).fill(null).map((_, i) => [
            fmt(subWeeks(baseDateRange.startDate, i + 1)),
            fmt(subWeeks(baseDateRange.endDate, i + 1)),
          ]);
          return [ranges[c - 1][0], ranges[0][1]];
        }
        default:
          return ["", ""];
      }

    case "day":
      switch (option) {
        case "previous":
          return [fmt(subDays(baseDateRange.startDate, 1)), fmt(subDays(baseDateRange.endDate, 1))];
        case "same_last": {
          const sr = subRange || "week";
          if (sr === "week") return [fmt(subDays(baseDateRange.startDate, 7)), fmt(subDays(baseDateRange.endDate, 7))];
          if (sr === "month") return [fmt(subMonths(baseDateRange.startDate, 1)), fmt(subMonths(baseDateRange.endDate, 1))];
          if (sr === "year") return [fmt(subYears(baseDateRange.startDate, 1)), fmt(subYears(baseDateRange.endDate, 1))];
          return [fmt(subDays(baseDateRange.startDate, 7)), fmt(subDays(baseDateRange.endDate, 7))];
        }
        case "last_n": {
          const c = count || 2;
          const ranges = Array(c).fill(null).map((_, i) => [
            fmt(subDays(baseDateRange.startDate, i + 1)),
            fmt(subDays(baseDateRange.endDate, i + 1)),
          ]);
          return [ranges[c - 1][0], ranges[0][0]];
        }
        case "custom": {
          if (customFrom) {
            const d = parse(customFrom, "yyyy-MM-dd", new Date());
            return [fmt(d), fmt(addDays(d, 1))];
          }
          return ["", ""];
        }
        default:
          return ["", ""];
      }

    default:
      return ["", ""];
  }
};

export const formatComparisonApiUrl = ({
  baseUrl,
  mode,
  comparisonDateRange,
  dateRange,
  timeRange,
  timezoneOffset,
  widgetFilters,
  locationFilters,
  dimension,
}: {
  baseUrl: string;
  mode: ComparisonMode;
  comparisonDateRange: [string, string];
  dateRange: [string, string];
  timeRange: [[number, number], [number, number]];
  timezoneOffset: string;
  widgetFilters: any[];
  locationFilters: number[];
  dimension: string | null;
}): string => {
  const params: string[] = [];

  if (dateRange[0] && comparisonDateRange.length === 2) {
    const baseTimeRange = convertDateRangeToEpochTimestamp(dateRange, timeRange, timezoneOffset);
    const compareTimeRange = convertDateRangeToEpochTimestamp(comparisonDateRange, timeRange, timezoneOffset);
    params.push(`base_time_range=${baseTimeRange[0]}:${baseTimeRange[1]}`);
    params.push(`compare_time_range=${compareTimeRange[0]}:${compareTimeRange[1]}`);
  }

  if (widgetFilters.length > 0) {
    for (const filter of widgetFilters) {
      const ids = filter.data.map((w: any) => w.id);
      params.push(stringify({ [filter.key]: ids }, { arrayFormat: "comma" }));
    }
  }

  if (locationFilters.length > 0) {
    params.push(stringify({ locations: locationFilters }, { arrayFormat: "comma" }));
  }

  if (dimension) {
    params.push(`compare-dimension=${dimension}`);
  }

  const queryString = params.length > 0 ? `?${params.join("&")}` : "";
  return `${baseUrl}/${mode}${queryString}`;
};
